import { useCallback, useEffect, useState } from 'react';
import { Ban, FileSignature, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { billingApi, type MandateEntityOption, type SepaMandate } from '../../../lib/api/billing';
import {
  MANDATE_CHANNEL_LABEL,
  MANDATE_STATUS,
  SEPA_PROBLEM_LABEL,
  formatIban,
  formatIsoDate,
  ibanProblem,
  todayIso,
} from '../../../lib/billing';

interface Props {
  personId: string | number;
  personName: string;
  canEdit: boolean;
  /** Nach Anlage oder Widerruf: Vertragskonten und Zahlungsmittel neu laden. */
  onChanged: () => Promise<void>;
}

/** SEPA-Mandate der Person gegenüber den abrechnenden Einheiten (ADR-0003, Phase 3). */
export default function SepaMandatesPanel({ personId, personName, canEdit, onChanged }: Props) {
  const [mandates, setMandates] = useState<SepaMandate[]>([]);
  const [entities, setEntities] = useState<MandateEntityOption[]>([]);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<SepaMandate | null>(null);
  const [showing, setShowing] = useState<SepaMandate | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await billingApi.listMandates(personId);
      setMandates(Array.isArray(response?.items) ? response.items : []);
      setEntities(Array.isArray(response?.entities) ? response.entities : []);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Mandate konnten nicht geladen werden');
    }
  }, [personId]);

  useEffect(() => {
    void load();
  }, [load]);

  const entityName = (id: number) => entities.find((entity) => entity.id === id)?.name ?? `Einheit #${id}`;
  const sepaEntities = entities.filter((entity) => entity.isActive && !entity.sepaProblem);
  const visible = mandates.filter((mandate) => mandate.status === 'active' || mandates.length <= 3);

  if (mandates.length === 0 && (!canEdit || sepaEntities.length === 0)) return null;

  const openText = async (mandate: SepaMandate) => {
    try {
      setShowing(await billingApi.getMandate(mandate.id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Mandat konnte nicht geladen werden');
    }
  };

  return (
    <div className="space-y-2 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium">SEPA-Mandate</h4>
          <p className="text-xs text-muted-foreground">Lastschrift über die Hausbank der abrechnenden Einheit</p>
        </div>
        {canEdit && sepaEntities.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Mandat erfassen
          </Button>
        )}
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch kein Mandat.</p>
      ) : (
        <ul className="divide-y">
          {visible.map((mandate) => (
            <li key={mandate.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                  <span className="tabular-nums">{mandate.reference}</span>
                  <Badge variant={MANDATE_STATUS[mandate.status]?.variant ?? 'outline'} size="sm">
                    {MANDATE_STATUS[mandate.status]?.label ?? mandate.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {entityName(mandate.billingEntityId)} · {mandate.accountHolder} · {mandate.ibanMasked} · erteilt{' '}
                  {formatIsoDate(mandate.signedOn)} ({MANDATE_CHANNEL_LABEL[mandate.channel] ?? mandate.channel})
                  {mandate.lastCollectedOn && ` · zuletzt eingezogen ${formatIsoDate(mandate.lastCollectedOn)}`}
                  {mandate.revokedReason && ` · ${mandate.revokedReason}`}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => void openText(mandate)}>
                  <FileSignature className="mr-1 h-4 w-4" />
                  Mandatstext
                </Button>
                {canEdit && mandate.status === 'active' && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setRevoking(mandate)}>
                    <Ban className="mr-1 h-4 w-4" />
                    Widerrufen
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <MandateDialog
          personId={personId}
          personName={personName}
          entities={sepaEntities}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await load();
            await onChanged();
          }}
        />
      )}

      {revoking && (
        <RevokeDialog
          mandate={revoking}
          onClose={() => setRevoking(null)}
          onRevoked={async () => {
            setRevoking(null);
            await load();
            await onChanged();
          }}
        />
      )}

      {showing && (
        <Dialog open onOpenChange={(open) => !open && setShowing(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Mandat {showing.reference}</DialogTitle>
              <DialogDescription>Wortlaut, dem bei der Erteilung zugestimmt wurde.</DialogDescription>
            </DialogHeader>
            <pre className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 font-sans text-sm">{showing.mandateText}</pre>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface MandateDialogProps {
  personId: string | number;
  personName: string;
  entities: MandateEntityOption[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}

function MandateDialog({ personId, personName, entities, onClose, onCreated }: MandateDialogProps) {
  const [entityId, setEntityId] = useState(entities.length === 1 ? String(entities[0].id) : '');
  const [holder, setHolder] = useState(personName);
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [signedOn, setSignedOn] = useState(todayIso());
  const [confirmed, setConfirmed] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const ibanError = iban.trim() ? ibanProblem(iban) : null;

  useEffect(() => {
    if (!entityId) {
      setText(null);
      return;
    }
    let cancelled = false;
    billingApi
      .mandateText(Number(entityId))
      .then((response) => !cancelled && setText(response.text))
      .catch(() => !cancelled && setText(null));
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  const save = async () => {
    setSaving(true);
    try {
      const mandate = await billingApi.createMandate(personId, {
        billingEntityId: Number(entityId),
        accountHolder: holder.trim(),
        iban: iban.trim(),
        bic: bic.trim() || undefined,
        signedOn,
      });
      toast.success(`Mandat ${mandate.reference} erfasst – offene Vertragskonten ziehen jetzt per Lastschrift ein`);
      await onCreated();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Mandat konnte nicht erfasst werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>SEPA-Mandat erfassen</DialogTitle>
          <DialogDescription>
            Für ein unterschriebenes Mandat (Papier oder PDF). Eine neue Bankverbindung ersetzt ein bestehendes
            Mandat bei derselben Einheit.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="mandate-entity">Zahlungsempfänger</Label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger id="mandate-entity">
                <SelectValue placeholder="Abrechnende Einheit wählen" />
              </SelectTrigger>
              <SelectContent>
                {entities.map((entity) => (
                  <SelectItem key={entity.id} value={String(entity.id)}>
                    {entity.name}
                    {entity.sepaProblem ? ` (${SEPA_PROBLEM_LABEL[entity.sepaProblem] ?? entity.sepaProblem})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mandate-holder">Kontoinhaber</Label>
            <Input id="mandate-holder" maxLength={70} value={holder} onChange={(event) => setHolder(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mandate-signed">Unterschrieben am</Label>
            <Input
              id="mandate-signed"
              type="date"
              max={todayIso()}
              value={signedOn}
              onChange={(event) => setSignedOn(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mandate-iban">IBAN</Label>
            <Input
              id="mandate-iban"
              autoComplete="off"
              value={iban}
              onChange={(event) => setIban(event.target.value)}
              onBlur={() => iban.trim() && !ibanError && setIban(formatIban(iban))}
              aria-invalid={Boolean(ibanError)}
            />
            {ibanError && <p className="text-sm text-destructive">{ibanError}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="mandate-bic">BIC (optional)</Label>
            <Input id="mandate-bic" value={bic} onChange={(event) => setBic(event.target.value.toUpperCase())} />
          </div>
          {text && (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 font-sans text-xs md:col-span-2">
              {text}
            </pre>
          )}
          <label className="flex items-start gap-3 text-sm md:col-span-2">
            <Checkbox checked={confirmed} onCheckedChange={(checked) => setConfirmed(checked === true)} />
            Ein vom Kontoinhaber unterschriebenes Mandat mit diesem Wortlaut liegt vor und wird aufbewahrt.
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !confirmed || !entityId || !holder.trim() || !iban.trim() || Boolean(ibanError)}
          >
            {saving ? 'Speichert …' : 'Mandat erfassen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RevokeDialogProps {
  mandate: SepaMandate;
  onClose: () => void;
  onRevoked: () => Promise<void>;
}

function RevokeDialog({ mandate, onClose, onRevoked }: RevokeDialogProps) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const revoke = async () => {
    setSaving(true);
    try {
      await billingApi.revokeMandate(mandate.id, reason.trim() || undefined);
      toast.success(`Mandat ${mandate.reference} widerrufen`);
      await onRevoked();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Widerruf fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mandat widerrufen?</DialogTitle>
          <DialogDescription>
            {mandate.reference} ({mandate.ibanMasked}) wird nicht mehr eingezogen. Offene Posten bleiben offen, bis
            ein neues Zahlungsmittel hinterlegt ist. Bereits exportierte Läufe sind nicht betroffen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="revoke-reason">Grund (optional)</Label>
          <Input
            id="revoke-reason"
            maxLength={255}
            placeholder="z. B. Widerruf per E-Mail vom 12.10."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Zurück
          </Button>
          <Button variant="destructive" onClick={() => void revoke()} disabled={saving}>
            Widerrufen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
